<template>
  <div
    id="droparea"
    :class="['drop-area', { 'is-loading': loading }]"
    @dragover.prevent="dragOver"
    @dragleave.prevent="dragLeave"
    @drop.prevent="!loading && onFileDrop($event)"
  >
    <!-- Drag Overlay -->
    <div v-if="!loading">
      <div v-if="isDragging" class="drag-overlay">
        <v-icon x-large color="primary">mdi-upload</v-icon>
        <p v-html="dragoverText"></p>
      </div>

      <!-- Content when not dragging -->
      <div v-else class="content">
        <slot name="dialog">
          <p v-html="text"></p>
        </slot>
        <span>{{ $t("or") }}</span>
        <a href="#" class="upload-link" @click.prevent="triggerFileInput">
          {{ selectFilesText || $t("selectFiles") }}
        </a>
        <span class="caption">{{ $t("components.DropAreaMulti.maxFiles", { value: maxFiles }) }}</span>
        <input
          type="file"
          ref="fileInput"
          class="hidden-file-input"
          @change="onFileSelect"
          :multiple="maxFiles !== 1"
          :accept="accept"
        />
      </div>
    </div>
    <div v-else>
      <v-card class="px-4 mt-16 mb-4">
        <v-progress-linear :value="loadingProgress" color="primary" height="4" absolute></v-progress-linear>
      </v-card>
    </div>
    <template v-if="!isDragging">
      <slot name="chips">
        <div v-if="filesModel.length" class="chip-list">
          <v-chip
            v-for="(file, i) in filesModel"
            :key="i"
            small
            close
            @click:close="removeFile(i)"
            style="margin: 2px"
          >{{ file.name }}</v-chip>
        </div>
      </slot>
    </template>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

@Component
export default class DropAreaMulti extends Vue {
  @Prop({ type: String, default: "" })
  dragoverText!: string;

  @Prop({ type: String, default: "" })
  text!: string;

  @Prop({ type: Number, default: 1000 })
  maxFiles!: number;

  @Prop({ type: Boolean, default: false })
  loading!: boolean;

  @Prop({ type: Number, default: 0 })
  loadingProgress!: number;

  @Prop({ type: String, default: ".webp, .jpg, .jpeg, .png" })
  accept!: string;

  @Prop()
  files!: File[];

  @Prop()
  selectFilesText?: string;

  get filesModel() {
    return this.files;
  }

  set filesModel(v: File[]) {
    this.$emit("update:files", v);
  }

  isDragging = false;

  dragOver(event: DragEvent) {
    if (!this.loading && event.dataTransfer) {
      this.isDragging = true;
      event.dataTransfer.dropEffect = "copy";
    }
  }

  dragLeave() {
    if (!this.loading) {
      this.isDragging = false;
    }
  }

  async onFileDrop(event: DragEvent) {
    if (this.loading) return;
    this.isDragging = false;
    const dataTransfer = event.dataTransfer;
    if (!dataTransfer) return;

    const files = Array.from(dataTransfer.files);
    if (files.length === 0) return;

    this.handleFile(files);
  }

  triggerFileInput() {
    if (!this.loading) {
      (this.$refs.fileInput as HTMLInputElement).click();
    }
  }

  onFileSelect(event: Event) {
    if (this.loading) return;
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const files = Array.from(input.files);
      this.handleFile(files);
    }
  }

  emitUploadedFile(files: File[]) {
    this.$emit("droped", files);
    this.$emit("dropped", files);
  }

  removeFile(i: number) {
    const updated = [...this.filesModel];
    updated.splice(i, 1);
    this.filesModel = updated;
    this.emitUploadedFile(updated);
  }

  handleFile(files: File[]) {
    const invalidFiles = files.filter((file) => !this.isImage(file));

    if (invalidFiles.length > 0) {
      this.displayNotAnImage();
      return;
    }

    const remaining = Math.max(0, this.maxFiles - this.filesModel.length);
    const toAdd = files.slice(0, remaining);

    if (toAdd.length < files.length) {
      this.$toast.warning(this.$t("components.DropAreaMulti.maxFilesReached", { value: this.maxFiles }));
    }

    const newFiles = [...this.filesModel, ...toAdd];
    this.filesModel = newFiles;
    this.emitUploadedFile(newFiles);
  }

  isImage(file: File) {
    const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    return this.accept.includes(fileExtension);
  }

  displayNotAnImage() {
    this.$toast.error(this.$t("components.DropAreaMulti.fileTypeError") + " (" + this.accept + ")");
  }
}
</script>

<style scoped>
.drop-area {
  position: relative;
  border: 2px dashed #42a5f5;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  transition:
    background-color 0.3s ease,
    border-color 0.3s ease;
  min-height: 250px;
  width: 100%;
  box-sizing: border-box;
  opacity: 1;
  pointer-events: all;
}

.is-loading {
  opacity: 0.6;
  pointer-events: none;
}

.drag-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(227, 242, 253, 0.8);
  border-radius: 8px;
  z-index: 1;
  pointer-events: none;
}

.content {
  z-index: 0;
}

.chip-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 8px;
}

.content p {
  font-size: 1.1em;
  color: #757575;
  margin-bottom: 8px;
}

.content span {
  display: block;
  margin: 10px 0;
}

.upload-link {
  color: #1e88e5;
  text-decoration: none;
  font-weight: bold;
}

.upload-link:hover {
  text-decoration: underline;
}

.hidden-file-input {
  display: none;
}
</style>
